import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Users, ArrowRight } from "lucide-react";

const DiscordIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.885 3.3a.075.075 0 0 0-.079.037c-.34.6-.719 1.383-.984 2.005a18.27 18.27 0 0 0-5.487 0A12.5 12.5 0 0 0 9.34 3.337.077.077 0 0 0 9.261 3.3 19.72 19.72 0 0 0 5.83 4.369a.07.07 0 0 0-.032.027C2.548 9.043 1.635 13.579 2.079 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.994 3.03.078.078 0 0 0 .085-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.104 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .079.01c.12.099.246.197.373.291a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.076.076 0 0 0-.041.105c.36.699.772 1.363 1.225 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .031-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.955 2.418-2.157 2.418zm7.974 0c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

/**
 * Card promoting our Discord server on the Home page. Fetches live member count
 * via /api/config/server-info (backend-cached, 60s). Silently hides if the widget
 * isn't enabled on the guild or the guild id isn't configured.
 */
export default function DiscordServerCard() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/config/server-info");
        if (alive && data && data.enabled) setInfo(data);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  if (!info) return null;
  return (
    <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16" data-testid="discord-server-card">
      <div className="grid md:grid-cols-12 gap-6 items-stretch">
        <div className="md:col-span-8 relative overflow-hidden p-10 lg:p-14" style={{ background: "linear-gradient(135deg, #5865F2 0%, #4752C4 100%)" }}>
          <div className="absolute -right-10 -bottom-10 opacity-20">
            <DiscordIcon size={220} />
          </div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-6 text-white/90 label-eyebrow">
              <DiscordIcon size={16} />
              <span>▮ COMMUNITY / DISCORD</span>
            </div>
            <h2 className="font-display text-4xl lg:text-6xl text-white leading-none mb-4">
              Join <span className="whitespace-nowrap">{info.name}.</span>
            </h2>
            <p className="text-white/80 max-w-xl text-sm lg:text-base">
              Chat with hosts, get show reminders straight to your DMs, and unlock streak badges for tuning in every week.
            </p>
            <Link
              to="/login"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-white text-[#5865F2] font-mono-ui text-xs uppercase tracking-[0.18em] hover:bg-black hover:text-white transition-colors"
              data-testid="discord-signin-btn"
            >
              <DiscordIcon size={14} /> Sign In <ArrowRight size={12} />
            </Link>
          </div>
        </div>
        <div className="md:col-span-4 bg-[var(--ff-surface)] border border-[var(--ff-border)] p-8 flex flex-col justify-between">
          <div>
            <div className="label-eyebrow mb-2 text-[var(--ff-red)]">▮ LIVE ROSTER</div>
            <div className="font-display text-6xl lg:text-7xl leading-none mt-4" data-testid="discord-member-count">
              {info.presence_count}
            </div>
            <div className="font-mono-ui text-xs uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-2 flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32D74B] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#32D74B]"></span>
              </span>
              Online right now
            </div>
          </div>
          <div className="border-t border-[var(--ff-border)] pt-4 mt-6 flex items-center gap-2 text-[var(--ff-text-dim)] font-mono-ui text-[0.65rem] uppercase tracking-[0.15em]">
            <Users size={12} /> {info.member_count}+ members total
          </div>
        </div>
      </div>
    </section>
  );
}
